import { useEffect, useState } from "react";
import { Accessibility, X, RotateCcw } from "lucide-react";

const OPTIONS = [
  { key: "a11y-large-text", label: "Testo più grande" },
  { key: "a11y-xl-text", label: "Testo molto grande" },
  { key: "a11y-contrast", label: "Alto contrasto" },
  { key: "a11y-underline-links", label: "Sottolinea link" },
  { key: "a11y-reduce-motion", label: "Riduci animazioni" },
];

const STORAGE = "a11y_prefs";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({});

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE) || "{}");
      setPrefs(saved);
      applyPrefs(saved);
    } catch {
      // ignore
    }
  }, []);

  const applyPrefs = (p) => {
    const html = document.documentElement;
    OPTIONS.forEach((o) => html.classList.toggle(o.key, !!p[o.key]));
  };

  const toggle = (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    // large vs xl are mutually exclusive
    if (key === "a11y-large-text" && next[key]) next["a11y-xl-text"] = false;
    if (key === "a11y-xl-text" && next[key]) next["a11y-large-text"] = false;
    setPrefs(next);
    applyPrefs(next);
    localStorage.setItem(STORAGE, JSON.stringify(next));
  };

  const reset = () => {
    setPrefs({});
    applyPrefs({});
    localStorage.removeItem(STORAGE);
  };

  return (
    <>
      <button
        data-testid="accessibility-toggle"
        aria-label="Opzioni di accessibilità"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 left-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-700 text-white shadow-[0_10px_30px_rgba(16,185,129,0.25)] transition-colors duration-200 hover:bg-emerald-600"
      >
        <Accessibility className="h-5 w-5" />
      </button>

      {open ? (
        <div
          data-testid="accessibility-panel"
          role="dialog"
          aria-label="Accessibilità"
          className="fixed bottom-20 left-5 z-[60] w-[280px] rounded-2xl border border-black/10 bg-[var(--ink-850)] p-4 shadow-2xl"
        >
          <div className="flex items-center justify-between">
            <span className="font-display text-sm font-semibold text-[color:var(--text-primary)]">
              Accessibilità
            </span>
            <button
              aria-label="Chiudi"
              onClick={() => setOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-black/10 text-secondary hover:text-[color:var(--text-primary)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-3 space-y-1.5">
            {OPTIONS.map((o) => (
              <button
                key={o.key}
                data-testid={`a11y-opt-${o.key}`}
                onClick={() => toggle(o.key)}
                aria-pressed={!!prefs[o.key]}
                className={`flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-colors duration-200 ${
                  prefs[o.key]
                    ? "border-emerald-400/40 bg-emerald-600/10 text-[color:var(--text-primary)]"
                    : "border-black/10 bg-black/[0.02] text-secondary hover:border-black/15"
                }`}
              >
                {o.label}
                <span
                  className={`h-4 w-4 rounded-full border ${
                    prefs[o.key]
                      ? "border-emerald-400 bg-emerald-400"
                      : "border-black/20"
                  }`}
                />
              </button>
            ))}
          </div>

          <button
            onClick={reset}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-black/10 py-2 text-xs text-tertiary hover:text-secondary"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Ripristina
          </button>
        </div>
      ) : null}
    </>
  );
}
